import React, { useRef, useState } from 'react';
import s from '../../styles/AdminDocumentMap.module.css';
import { getRecurrenceText } from 'utils/documentUtils';
import { TemplateInfo } from '../../types/AdminTypes';

export interface DocumentTypeRow {
  id: string;
  name: string;
  recurrence: string;
  multi_uploads: boolean;
  viewingRole: string;
  edittingRole: string;
}

interface DocumentTypeTableProps {
  rows: DocumentTypeRow[];
  onSave: (row: DocumentTypeRow) => Promise<void>;
  onDelete: (id: string) => void;
  categoryId?: string;
  subcategoryId?: string;
  templatesMap?: Record<string, TemplateInfo>;
  onUploadTemplate?: (file: File, documentTypeId: string, categoryId: string, subcategoryId?: string) => Promise<void> | void;
  onDownloadTemplate?: (templateId: string) => void;
  onDeleteTemplate?: (templateId: string, documentTypeId: string) => void;
}

const DocumentTypeTable: React.FC<DocumentTypeTableProps> = ({
  rows,
  onSave,
  onDelete,
  categoryId,
  subcategoryId,
  templatesMap = {},
  onUploadTemplate,
  onDownloadTemplate,
  onDeleteTemplate,
}) => {
  const [editingRow, setEditingRow] = useState<DocumentTypeRow | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetId = useRef<string | null>(null);

  const handleSave = async () => {
    if (!editingRow) return;
    const rowToSave = editingRow;
    setEditingRow(null);
    await onSave(rowToSave);
  };

  const handleUploadClick = (documentTypeId: string) => {
    uploadTargetId.current = documentTypeId;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && uploadTargetId.current && categoryId) {
      onUploadTemplate?.(file, uploadTargetId.current, categoryId, subcategoryId);
    }
    e.target.value = '';
  };

  const getRoleText = (role: string) => {
    switch(role) {
      case "agent":   return "Agent";
      case "manager": return "Manager";
      case "admin":   return "Admin";
      default:        return role || "—";
    }
  };

  if (rows.length === 0) return <p className={s.emptyState}>None yet.</p>;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
        onChange={handleFileChange}
      />
      <table className={s.table}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Recurrence</th>
            <th>Multiple Uploads</th>
            <th>Viewing Role</th>
            <th>Editting Role</th>
            <th>Template</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const template = templatesMap[row.id] ?? null;
            return (
              <tr key={row.id}>
                {editingRow?.id === row.id ? (
                  <>
                    <td>
                      <input
                        className={s.inlineInput}
                        value={editingRow.name}
                        onChange={e => setEditingRow({ ...editingRow, name: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditingRow(null); }}
                        autoFocus
                      />
                    </td>
                    <td>
                      <select
                        className={s.inlineInput}
                        value={editingRow.recurrence}
                        onChange={e => setEditingRow({ ...editingRow, recurrence: e.target.value })}
                      >
                        <option value="none">None</option>
                        <option value="1_month">1 Month</option>
                        <option value="3_month">3 Months</option>
                        <option value="1_year">1 Year</option>
                        <option value="3_year">3 Years</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={editingRow.multi_uploads}
                        onChange={e => setEditingRow({ ...editingRow, multi_uploads: e.target.checked })}
                      />
                    </td>
                    <td>
                      <select
                        className={s.inlineInput}
                        value={editingRow.viewingRole}
                        onChange={e => setEditingRow({ ...editingRow, viewingRole: e.target.value })}
                      >
                        <option value="agent">Agent</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td>
                      <select
                        className={s.inlineInput}
                        value={editingRow.edittingRole}
                        onChange={e => setEditingRow({ ...editingRow, edittingRole: e.target.value })}
                      >
                        <option value="agent">Agent</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td>—</td>
                    <td>
                      <button className={`${s.btnSm} ${s.btnPrimary}`} onClick={handleSave}>Save</button>
                      <button className={`${s.btnSm} ${s.btnSecondary}`} onClick={() => setEditingRow(null)}>Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{row.name}</td>
                    <td>{getRecurrenceText(row.recurrence)}</td>
                    <td>{row.multi_uploads ? 'Yes' : 'No'}</td>
                    <td>{getRoleText(row.viewingRole)}</td>
                    <td>{getRoleText(row.edittingRole)}</td>
                    <td className={s.templateCell}>
                      {template ? (
                        <span className={s.templateActions}>
                          <span className={s.templateName} title={`${template.doc_name} (${template.fileType})`}>
                            {template.doc_name}
                          </span>
                          <button
                            className={s.iconBtn}
                            title="Download template"
                            onClick={() => onDownloadTemplate?.(template._id)}
                          >
                            ⬇️
                          </button>
                          <button
                            className={`${s.iconBtn} ${s.iconBtnDanger}`}
                            title="Remove template"
                            onClick={() => onDeleteTemplate?.(template._id, row.id)}
                          >
                            ✕
                          </button>
                        </span>
                      ) : (
                        <button
                          className={s.iconBtn}
                          title="Upload template"
                          onClick={() => handleUploadClick(row.id)}
                        >
                          ➕
                        </button>
                      )}
                    </td>
                    <td>
                      <button className={s.iconBtn} title="Edit" onClick={() => setEditingRow({ ...row })}>✏️</button>
                      <button className={`${s.iconBtn} ${s.iconBtnDanger}`} title="Delete" onClick={() => onDelete(row.id)}>🗑️</button>
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export default DocumentTypeTable;